import React, { useState, useRef } from 'react';
import { apiFetch } from '../utils/apiUtils';
import { toast } from 'react-toastify';

const ROUTE_LABELS = {
  'jerusalem-newyork': 'Jerusalem → Milan → New York',
  'jerusalem-milan': 'Jerusalem → Milan',
};

export default function TicketActionModal({ reg, flightConfigs, accessCode, onClose, onSuccess }) {
  const [actionType, setActionType] = useState(null); // null | 'cancel' | 'transfer' | 'replace'
  const [step, setStep] = useState('choose'); // choose | form | confirm | processing | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // New passenger form (transfer/replace)
  const [np, setNp] = useState({
    first_name: '', last_name: '', birth_date: '', gender: 'Male',
    email: '', phone: '', school: '',
    passport_number: '', passport_country: 'United States', passport_expiration: '',
  });
  const [passportFile, setPassportFile] = useState(null);
  const [passportFileName, setPassportFileName] = useState('');
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const fileInputRef = useRef(null);

  const flightConfig = flightConfigs?.[reg.flight_id] || {};
  const paymentProvider = flightConfig.paymentProvider || 'stripe';
  const flightLabel = flightConfig.label || reg.flight_id;
  const routeLabel = ROUTE_LABELS[reg.route] || reg.route;
  const amountCharged = parseFloat(reg.amount_charged) || 0;
  const refundAmount = Math.max(amountCharged - 150, 0);
  const routeKey = reg.route;
  const newPrice = flightConfig.routes?.[routeKey]?.price || amountCharged;

  const updateNp = (field, value) => setNp(prev => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25MB)');
      return;
    }
    setPassportFile(file);
    setPassportFileName(file.name);
  };

  const uploadPassport = async () => {
    if (!passportFile) return null;
    setUploadingPassport(true);
    try {
      const ext = passportFile.name.split('.').pop() || 'pdf';
      const safeName = `${np.last_name || 'unknown'}-${np.first_name || 'unknown'}`.replace(/[^a-zA-Z0-9-]/g, '');
      const res = await apiFetch(`${import.meta.env.VITE_API_URL}/flight-upload-url?fileName=${encodeURIComponent(safeName + '.' + ext)}&contentType=${encodeURIComponent(passportFile.type)}`);
      if (res.status !== 'success') throw new Error(res.message || 'Failed to get upload URL');

      const putRes = await fetch(res.presignedUrl, { method: 'PUT', body: passportFile, headers: { 'Content-Type': passportFile.type } });
      if (!putRes.ok) throw new Error('Upload failed');

      return res.s3Key;
    } catch (err) {
      toast.error('Passport upload failed: ' + err.message);
      return null;
    } finally {
      setUploadingPassport(false);
    }
  };

  const handleSubmit = async () => {
    setStep('processing');
    setError('');

    try {
      let passportS3Key = null;
      if ((actionType === 'transfer' || actionType === 'replace') && passportFile) {
        passportS3Key = await uploadPassport();
        if (!passportS3Key) { setStep('form'); return; }
      }

      const payload = {
        access_code: accessCode,
        action: actionType,
        registrationId: reg.id,
      };
      if (actionType !== 'cancel') {
        payload.newPassenger = np;
        payload.passportS3Key = passportS3Key;
      }

      const res = await apiFetch(`${import.meta.env.VITE_API_URL}/flight-admin/ticket-action`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (res.status === 'success') {
        setResult(res);
        setStep('done');
        toast.success(res.message);
      } else {
        setError(res.message || 'Action failed');
        setStep('form');
        toast.error(res.message || 'Action failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
      setStep('form');
      toast.error(err.message || 'Network error');
    }
  };

  const selectAction = (type) => {
    setActionType(type);
    setStep(type === 'cancel' ? 'confirm' : 'form');
  };

  const goBack = () => {
    if (step === 'confirm') setStep(actionType === 'cancel' ? 'choose' : 'form');
    else if (step === 'form') { setStep('choose'); setActionType(null); }
  };

  // ─── RENDER ───

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-8 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Ticket Actions</h2>
            <p className="text-sm text-gray-500">{reg.first_name} {reg.last_name} — {flightLabel}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-5">
          {/* Passenger Summary — always visible */}
          <div className="bg-gray-50 rounded-lg p-4 mb-5 grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-500">Name:</span> <strong>{reg.first_name} {reg.last_name}</strong></div>
            <div><span className="text-gray-500">Email:</span> {reg.email}</div>
            <div><span className="text-gray-500">Flight:</span> {flightLabel}</div>
            <div><span className="text-gray-500">Route:</span> {routeLabel}</div>
            <div><span className="text-gray-500">Paid:</span> <strong>${amountCharged.toLocaleString()}</strong></div>
            <div><span className="text-gray-500">Provider:</span> {paymentProvider}</div>
          </div>

          {/* ─── STEP: Choose Action ─── */}
          {step === 'choose' && (
            <div className="space-y-3">
              <button onClick={() => selectAction('cancel')} className="w-full text-left p-4 rounded-lg border-2 border-red-200 hover:border-red-400 hover:bg-red-50 transition-all">
                <p className="font-bold text-red-700">Cancel & Refund</p>
                <p className="text-sm text-red-600 mt-1">Mark as cancelled, send refund email (${refundAmount.toLocaleString()} = ${amountCharged.toLocaleString()} - $150 fee)</p>
              </button>
              <button onClick={() => selectAction('transfer')} className="w-full text-left p-4 rounded-lg border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all">
                <p className="font-bold text-blue-700">Transfer (Name Change)</p>
                <p className="text-sm text-blue-600 mt-1">Transfer ticket to a new person. Payment stays — no refund, no new charge.</p>
              </button>
              <button onClick={() => selectAction('replace')} className="w-full text-left p-4 rounded-lg border-2 border-amber-200 hover:border-amber-400 hover:bg-amber-50 transition-all">
                <p className="font-bold text-amber-700">Replace (Cancel + New Payment)</p>
                <p className="text-sm text-amber-600 mt-1">Refund original passenger, send payment link to new person (${newPrice.toLocaleString()}).</p>
              </button>
            </div>
          )}

          {/* ─── STEP: Form (Transfer/Replace) ─── */}
          {step === 'form' && (actionType === 'transfer' || actionType === 'replace') && (
            <div>
              <h3 className="font-bold text-gray-800 mb-3">
                {actionType === 'transfer' ? 'New Passenger Details' : 'Replacement Passenger Details'}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
                  <input value={np.first_name} onChange={e => updateNp('first_name', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name *</label>
                  <input value={np.last_name} onChange={e => updateNp('last_name', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                  <input value={np.email} onChange={e => updateNp('email', e.target.value)} type="email" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input value={np.phone} onChange={e => updateNp('phone', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Birth Date</label>
                  <input value={np.birth_date} onChange={e => updateNp('birth_date', e.target.value)} type="date" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                  <select value={np.gender} onChange={e => updateNp('gender', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Passport Number</label>
                  <input value={np.passport_number} onChange={e => updateNp('passport_number', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Passport Country</label>
                  <input value={np.passport_country} onChange={e => updateNp('passport_country', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Passport Expiration</label>
                  <input value={np.passport_expiration} onChange={e => updateNp('passport_expiration', e.target.value)} type="date" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">School</label>
                  <input value={np.school} onChange={e => updateNp('school', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Passport Upload */}
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Passport Document</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => fileInputRef.current?.click()} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    {passportFileName ? 'Change File' : 'Upload Passport'}
                  </button>
                  {passportFileName && <span className="text-sm text-green-700 font-medium">{passportFileName}</span>}
                  <input ref={fileInputRef} type="file" accept="image/*,application/pdf" onChange={handleFileChange} className="hidden" />
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button onClick={goBack} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">← Back</button>
                <button
                  onClick={() => setStep('confirm')}
                  disabled={!np.first_name || !np.last_name || !np.email}
                  className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all ${
                    np.first_name && np.last_name && np.email
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Review & Confirm →
                </button>
              </div>
            </div>
          )}

          {/* ─── STEP: Confirm ─── */}
          {step === 'confirm' && (
            <div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <h3 className="font-bold text-red-800 mb-2">Confirm: {
                  actionType === 'cancel' ? 'Cancel & Refund' :
                  actionType === 'transfer' ? 'Ticket Transfer' : 'Cancel & Replace'
                }</h3>
                <ul className="text-sm text-red-700 space-y-1 list-disc pl-4">
                  <li><strong>{reg.first_name} {reg.last_name}</strong> will be marked as cancelled</li>
                  {actionType === 'cancel' && (
                    <>
                      <li>Refund email will be sent to {reg.email} for <strong>${refundAmount.toLocaleString()}</strong></li>
                      {paymentProvider === 'banquest' && <li>Email will request CVV for Banquest refund processing</li>}
                    </>
                  )}
                  {actionType === 'transfer' && (
                    <>
                      <li>Ticket transferred to <strong>{np.first_name} {np.last_name}</strong> ({np.email})</li>
                      <li>Payment of ${amountCharged.toLocaleString()} will be applied to the new booking</li>
                      <li>Confirmation email sent to new passenger</li>
                    </>
                  )}
                  {actionType === 'replace' && (
                    <>
                      <li>Refund of <strong>${refundAmount.toLocaleString()}</strong> to {reg.email}</li>
                      <li>Payment link for <strong>${newPrice.toLocaleString()}</strong> sent to {np.email}</li>
                      <li>New passenger: <strong>{np.first_name} {np.last_name}</strong></li>
                    </>
                  )}
                  <li>Office (office@igudys.com) will be notified</li>
                </ul>
              </div>

              {error && <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-4 text-sm text-red-700">{error}</div>}

              <div className="flex gap-3">
                <button onClick={goBack} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">← Back</button>
                <button onClick={handleSubmit} className="flex-1 py-3 rounded-lg font-bold text-sm bg-red-600 hover:bg-red-700 text-white transition-all">
                  Confirm & Execute
                </button>
              </div>
            </div>
          )}

          {/* ─── STEP: Processing ─── */}
          {step === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">
                {uploadingPassport ? 'Uploading passport...' : 'Processing ticket action...'}
              </p>
            </div>
          )}

          {/* ─── STEP: Done ─── */}
          {step === 'done' && result && (
            <div className="text-center py-4">
              <div className="text-4xl mb-3">&#10003;</div>
              <h3 className="text-lg font-bold text-green-700 mb-2">Action Completed</h3>
              <p className="text-gray-600 mb-4">{result.message}</p>

              {result.details?.refundAmount != null && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
                  <span className="text-gray-500">Refund:</span>{' '}
                  <strong>${result.details.refundAmount.toLocaleString()}</strong>{' '}
                  <span className="text-gray-400">({result.details.paymentProvider})</span>
                </div>
              )}
              {result.details?.newRegId && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
                  <span className="text-gray-500">New Reg ID:</span> <strong>{result.details.newRegId}</strong>
                  {result.details.newRegCode && <span className="text-gray-400 ml-2">({result.details.newRegCode})</span>}
                </div>
              )}
              {result.details?.checkoutUrl && (
                <div className="bg-blue-50 rounded-lg p-3 mb-3 text-sm">
                  <span className="text-blue-600">Payment link:</span>{' '}
                  <a href={result.details.checkoutUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline break-all text-xs">{result.details.checkoutUrl}</a>
                </div>
              )}
              {result.details?.paymentRef && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
                  <span className="text-gray-500">Payment Ref:</span>{' '}
                  <span className="text-xs font-mono break-all">{result.details.paymentRef}</span>
                </div>
              )}
              <p className="text-sm text-gray-400 mt-2">{result.details?.emailsSent || 0} email(s) sent</p>

              <button onClick={() => { onSuccess?.(); onClose(); }} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
